import React, { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { useLocation } from "@reach/router";

import usePopupSettings from "./usePopupSettings";
import usePopupTriggers, { forgetThisPageLoad } from "./usePopupTriggers";
import { clearLocks } from "./popupStorage";

const PopupOffer = lazy(() => import("./PopupOffer"));

const TRIGGERS = ["exit-intent", "inactivity"];

const popupParam = (search) =>
  search ? new URLSearchParams(search).get("popup") : null;

// ?popup=exit-intent / ?popup=inactivity previews a popup, ignoring the enable
// toggles and the limit. ?popup=reset drops the "already shown" locks instead.
const previewTrigger = (search) => {
  const requested = popupParam(search);

  return TRIGGERS.includes(requested) ? requested : null;
};

// Two triggers, one popup. Copy, toggles and timings all come from
// Theme Settings -> Popups in WordPress.
const SitePopups = () => {
  const settings = usePopupSettings();
  const { pathname, search } = useLocation();
  const preview = previewTrigger(search);
  const [trigger, setTrigger] = useState(null);
  const [tick, setTick] = useState(0);

  const onOpen = useCallback((next) => setTrigger(next), []);
  const onRetry = useCallback(() => setTick((value) => value + 1), []);
  const onClose = useCallback(() => setTrigger(null), []);

  useEffect(() => {
    if (preview) setTrigger(preview);
  }, [preview]);

  useEffect(() => {
    if (popupParam(search) !== "reset") return;
    clearLocks();
    forgetThisPageLoad();
    setTick((value) => value + 1);
  }, [search]);

  usePopupTriggers({
    settings: preview ? null : settings,
    pathname,
    isOpen: Boolean(trigger),
    onOpen,
    onRetry,
    tick,
  });

  const config =
    trigger === "exit-intent"
      ? settings?.exitIntentPopup
      : trigger === "inactivity"
      ? settings?.inactivityPopup
      : null;

  if (!config) return null;

  return (
    <Suspense fallback={null}>
      <PopupOffer config={config} trigger={trigger} onClose={onClose} />
    </Suspense>
  );
};

export default SitePopups;
